import React, { useCallback, useEffect, useState, useRef } from 'react';
import { connect } from 'react-redux';
import { getClients } from '../../../../../../services/clients';
import SearchSelect from '../../../../../CustomizedAntDesign/SearchSelect';

const SearchClientsInput = ({
  posType,
  onSelect,
  className,
  requireCollections = false,
  style,
  placeholder = 'Search client by name',
}) => {
  const [value, setValue] = useState(null);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [searchText, setSearchText] = useState('');
  const searchTimeoutRef = useRef(null);
  const requestIdRef = useRef(0);

  // Fetch clients from Elasticsearch endpoint
  const fetchClients = useCallback(async search => {
    const trimmedSearch = search?.trim() || '';
    if (trimmedSearch.length < 2) {
      setResults([]);
      setOptions([]);
      return;
    }

    // Track request ID to prevent race conditions
    requestIdRef.current += 1;
    const currentRequestId = requestIdRef.current;
    setLoading(true);

    try {
      const response = await getClients({
        data: {
          searchTerm: trimmedSearch,
          limit: 50,
          offset: 0,
          archived: false,
        },
        loadingStatusOff: true,
      });

      // Only update if this is still the latest request
      if (currentRequestId === requestIdRef.current) {
        setResults(response?.data || []);
      }
    } catch (error) {
      if (currentRequestId === requestIdRef.current) {
        console.error('Error fetching clients:', error);
        setResults([]);
      }
    } finally {
      if (currentRequestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, []);

  // Debounced search handler
  const handleSearch = useCallback(
    search => {
      setSearchText(search);

      // Clear previous timeout
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }

      // Debounce the search request
      searchTimeoutRef.current = setTimeout(() => {
        fetchClients(search);
      }, 300);
    },
    [fetchClients],
  );

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // Update options when results change
  useEffect(() => {
    const newOptions = results.flatMap(item => {
      if (requireCollections && !item.collections) return [];
      // ES returns 'refNum' instead of 'pos_ref_num'
      const suffix = (posType ? item.refNum : item.mobile) || item.mobile;
      const label = item.name || suffix;
      return {
        value: item?.id?.toString(),
        label,
        suffix,
      };
    });
    setOptions(newOptions);
  }, [results, posType, requireCollections]);

  const handleSelect = (_, option) => setValue(option);

  useEffect(() => {
    const selectedOption = results.find(e => e?.id?.toString() === value?.value);
    onSelect(selectedOption);
  }, [value, results, onSelect]);

  const emptyOptions = !loading && !options.length && searchText.length >= 2;
  const placeholderFormatted = emptyOptions ? 'No Clients' : placeholder;

  return (
    <SearchSelect
      value={value}
      options={options}
      loading={loading}
      filterOption={false}
      onSearch={handleSearch}
      onSelect={handleSelect}
      placeholder={placeholderFormatted}
      className={className || ''}
      showSearch
      style={style}
    />
  );
};

const mapStateToProps = state => ({
  posType: state.auth.pos_type,
});

export default connect(mapStateToProps, {})(SearchClientsInput);
